<style lang="scss">
  .print-on-track-left { width: 40%; }
  .print-on-track-right { width: 60%; }
  .print-inside-content {
    background: #bce4f6;
    -webkit-print-color-adjust: exact;
    padding-top: 10px;
    position: relative;
    min-height: 185px;
    padding-top: 50px;
    margin-top: 10px;
    border-top: 1px solid #b2b2b2;
    margin-bottom: 10px;
    border-bottom: 1px solid #b2b2b2;
    font-size: 110%;

    .bar {
      position: relative;
      max-width: 170px;
      text-align: center;

      .target-income {
        position: absolute;
        width: 100%;
        bottom: 100%;
        p { margin-bottom: 0; }
        span { font-size: 130%; }
      }

      .border {
        display: block;
        content: '';
        height: 2px;
        background: #000000;
      }

      .gap {
        width: 94%;
        margin: 0 auto;
        height: 30px;
        padding-top: 7px;
        color: #ffffff;
        background: #dd192e;
      }

      .income {
        width: 94%;
        margin: 0 auto;
        height: 75px;
        padding-top: 10px;
        color: #ffffff;
        background: #26559e;

        &.no-income-gap {
          height: 120px;
          padding-top: 30px;
        }
        span { font-size: 130%; }
      }
    }

    .gap-info {
      position: absolute;
      top: 0;
      left: 210px;
      width: 100%;
      max-width: 210px;
      background: #ffffff;
      height: 100%;
      padding: 10px 15px;
      font-size: 140%;

      p { line-height: 1.5; }
      span {
        background-color: #dd192e;
        color: #ffffff;
        padding: 3px 5px 3px 3px;
      }

      &::before {
        position: absolute;
        content: '';
        height: 20px;
        width: 20px;
        background: #ffffff;
        top: 15px;
        left: -10px;
        transform: rotate(45deg);
      }
    }

    .print-side-content {
      font-size: 140%;

      span {
        background-color: #fff;
        padding: 3px 5px 3px 3px;
      }
    }
  }

  .no-margin-bottom { margin-bottom: 0; }
</style>

<template>
  <section class="print-view">
    <div class="row">
      <div class="small-12 columns logo" v-html="logo"></div>
    </div>
    <div class="row">
      <div class="small-12 columns">
        <h1>Super Projection Calculator</h1>
        <h3>Your retirement income estimate - as at {{ todaysDate }}</h3>
        <p class="no-margin-bottom">This is a summary of your results from the QSuper Super Projection Calculator and includes some actions you could take to improve your retirement income. The assumptions used in this calculator are shown on the following pages.</p>

        <div class="row">
          <div class="small-12 columns print-inside-content">
            <div class="small-5 columns">
              <p class="print-side-content">You are on track to achieve a lump sum of <span>{{ yourBalanceAtRetirement | currencyDisplay }}</span> in retirement, which will give you an estimated annual income of <span>{{ yourEstimatedIncome | currencyDisplay }}</span> to age {{ inputs.RunOutAge.value }}.</p>
            </div>

            <div class="small-7 columns relative">
              <div class="income-gap-info">
                <div class="bar">
                  <div class="target-income">
                    <p class="small-line-height">Target retirement income <br /><span>{{ inputs.TargetIncome.value | currencyDisplay }}</span></p>
                  </div>
                  <div class="border"></div>

                  <div v-if="yourIncomeShortfall > 0">
                    <div class="gap" >Income Gap</div>
                    <div class="border"></div>
                  </div>
                  <div class="income" :class="{ 'no-income-gap': yourIncomeShortfall <= 0 }"><p class="small-line-height">Estimated annual<br />retirement income <br /><span>{{ yourEstimatedIncome | currencyDisplay }}</span></p></div>
                </div>
              </div>

              <div class="gap-info">
                <p v-if="yourIncomeShortfall > 0">However this is <span>{{ yourIncomeShortfall | currencyDisplay }}</span> LESS per year than your target!</p>
                <p v-else>Good news! Your estimated annual income is greater than your target.</p>
              </div>
            </div>
          </div>
        </div>

        <div class="summary-of-results">
          <h4>Summary of results</h4>
          <dl>
            <dt>Age you would like to retire</dt>
            <dd>{{ inputs.RetirementAge.value }}</dd>

            <dt>Age your partner would like to retire</dt>
            <dd>{{ inputs.RetirementAge_2.value }}</dd>

            <dt>Your balance at retirement</dt>
            <dd>{{ yourBalanceAtRetirement | currencyDisplay }}</dd>

            <dt>Your estimated annual income</dt>
            <dd>{{ yourIncomeAtRetirement | currencyDisplay }}</dd>

            <dt>Your desired annual income</dt>
            <dd>{{ inputs.TargetIncome.value | currencyDisplay }}</dd>

            <dt>Age you want your super to last to</dt>
            <dd>{{ inputs.RunOutAge.value }}</dd>

            <dt>Age pension entitlement</dt>
            <dd> {{ inputs.IncludeAgePension.value | boolYesNo }}</dd>
          </dl>
        </div>

        <hr />

        <div class="what-you-told-us">
          <h4>What you told us</h4>

          <div>
            <div class="header-partner">Your partner</div>
            <div class="header-you">You</div>

            <div class="header-context">General details</div>
            <dl>
              <display-print label="Age" property="AgeNow"></display-print>
              <display-print label="Gender" property="Sex"></display-print>
              <display-print label="Annual salary" property="Salary" filter="currencyDisplay"></display-print>
              <display-print label="Employer type" property="EmployerType" source="inferred" filter="memberType"></display-print>
            </dl>

            <div class="header-context">Contribution information</div>
            <dl>
              <display-print label="Member contribution" property="MemberContributions" source="inferred" filter="percentageDisplay"></display-print>
              <display-print label="Employer contribution" property="EmployerContributionRate" source="inferred" filter="percentageDisplay"></display-print>
              <display-print label="Salary sacrifice (before tax)" property="PreTaxMemberContributions" source="inferred" filter="currencyDisplay"></display-print>
              <display-print label="Personal (after tax)" property="PostTaxMemberContributions" source="inferred" filter="currencyDisplay"></display-print>
              <display-print label="One-off lump sum contribution" property="OneOffContribution" filter="currencyDisplay"></display-print>
              <display-print label="One-off contribution age" property="OneOffContributionMadeAtAge"></display-print>
            </dl>
          </div>

          <div>
            <div class="header-partner">Your partner</div>
            <div class="header-you">You</div>

            <div class="header-context">Current superannuation</div>
            <dl>
              <display-print label="Accumulation account" property="Accumulation" source="inferred" filter="currencyDisplay"></display-print>
              <!-- <display-print label="Defined benefit account" property="DefinedBenefit" source="inferred" filter="currencyDisplay"></display-print> -->
              <!-- <display-print label="Deferred retirement benefit" property="DeferredRetirementBalance" source="inputs" filter="currencyDisplay"></display-print> -->
              <display-print label="Other super" property="OtherFunds" source="inferred" filter="currencyDisplay"></display-print>
            </dl>

            <div class="header-context">Investment mix</div>
            <dl>
              <display-print label="Prior to retirement" property="InvestmentOption" filter="investmentMix"></display-print>
              <display-print label="In retirement" property="InvestmentOptionInRetirement" filter="investmentMix"></display-print>
            </dl>

            <div class="header-context">Other informaton</div>
            <dl>
              <display-print label="One-off purchase at retirement" property="WithdrawalAtRetirement" filter="currencyDisplay"></display-print>
              <display-print label="Change in working status" property="PartTimeWorkYearsDuration" filter="numYesNo"></display-print>
              <display-print label="Current value of investments outside of super?" property="OtherAssets" filter="currencyDisplay"></display-print>
            </dl>
          </div>
        </div>

        <div class="header-context">The amounts shown are in today's dollars.</div>

        <hr />

        <div class="what-can-you-do-now">
          <h4>What can you do now?</h4>
          <p>If your results are not quite what you hope for or you feel you would like to further maximise your income in retirement - explore the effect these actions could have on your income in retirement.</p>

          <div class="boxes">
            <div>
              <h5>Additional contributions</h5>
              <p>
                Contributing more into your super could
                have a significant impact on your end
                result.
              </p>
            </div>

            <div>
              <h5>Roll in your super</h5>
              <p>
                Consolidating your super may save you
                fees and make your super easier to keep
                track of.
              </p>
            </div>

            <div>
              <h5>Make an active investment choice</h5>
              <p>
                See what difference changing your
                investment mix both now and in
                retirement could have on your
                retirement income.
              </p>
            </div>

            <div>
              <h5>Revise your retirement target date</h5>
              <p>
                If you’re not on target, working longer,
                even in a part-time capacity, has the dual
                advantage of giving you longer to
                contribute to your super and reducingt he
                length of time you need your retirement
                lump sum to last.
              </p>
            </div>
          </div>
        </div>

        <h4>If you want to know more call us on 1300 360 750</h4>
      </div>
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex';
import DisplayPrint from '@/components/DisplayPrint.vue';

export default {
  components: {
    DisplayPrint,
  },
  data() {
    return {
      logo: require('!html-loader!@/assets/logo.svg'),
    };
  },
  computed: {
    ...mapState(['ui', 'inputs', 'inferred', 'results']),
    breakdownAge() {
      return this.ui.breakdown.age.value;
    },
    breakdownPartnerAge() {
      const yourAge = this.inputs.AgeNow.value;
      const partnerAge = this.inputs.AgeNow_2.value;
      const diff = yourAge - partnerAge;

      return this.breakdownAge - diff;
    },
    breakdownAgeIndex() {
      const min = this.showingIncomeGraph ? this.inputs.RetirementAge.value : this.inputs.AgeNow.value;
      const index = this.breakdownAge - min;
      return index;
    },
    yourBreakdownIndex() {
      return this.results.engineResults.subjectRetirementAgeIndex + this.breakdownAgeIndex;
    },
    yourSuper() {
      return this.results.engineResults.subjectOutput.deflatedDrawingsInYear[this.yourBreakdownIndex];
    },
    yourPension() {
      return this.results.engineResults.subjectOutput.deflatedAgePension[this.yourBreakdownIndex];
    },
    yourSalary() {
      return this.results.engineResults.subjectOutput.deflatedSalary[this.yourBreakdownIndex];
    },
    yourBalance() {
      return this.results.engineResults.subjectOutput.deflatedAssetsInYear[this.breakdownAgeIndex];
    },
    yourPartnerSuper() {
      return this.results.engineResults.partnerOutput.deflatedDrawingsInYear[this.yourBreakdownIndex];
    },
    yourPartnerPension() {
      return this.results.engineResults.partnerOutput.deflatedAgePension[this.yourBreakdownIndex];
    },
    yourPartnerSalary() {
      return this.results.engineResults.partnerOutput.deflatedSalary[this.yourBreakdownIndex];
    },
    yourPartnerBalance() {
      return this.results.engineResults.partnerOutput.deflatedAssetsInYear[this.breakdownAgeIndex];
    },
    yourTotalBalanceAtBreakdownAge() {
      return this.yourBalance + (this.includePartner ? this.yourPartnerBalance : 0);
    },
    // not breakdown properties
    yourIncomeShortfall() {
      return (this.yourEstimatedIncome - this.inferred.TargetIncome.value) * -1;
    },
    yourIncomeShortfallOrZero() {
      const shortFall = this.yourIncomeShortfall;
      return shortFall < 0 ? 0 : shortFall;
    },
    yourEstimatedIncome() {
      return this.results.targetIncome;
    },
    yourIncomeAtRetirement() {
      return this.results.deflatedCombinedIncomeAtRetirement;
    },
    yourBalanceAtRetirement() {
      return this.results.deflatedCombinedBalanceAtRetirement;
    },
    includePartner() {
      return this.inputs.IncludePartner.value;
    },
    todaysDate() {
      const today = new Date();
      const months = [
        'January', 'February', 'March',
        'April', 'May', 'June', 'July',
        'August', 'September', 'October',
        'November', 'December',
      ];

      return `${today.getDate()} ${months[today.getMonth()]} ${today.getFullYear()}`;
    },
  },
  mounted() {
    window.print();
  },
};
</script>
